import { FC, useCallback, useEffect, useState } from 'react'
import classNames from 'classnames'
import { Button } from '@/atoms/Button'
import { FlagIcon } from '@/atoms/Icons/FlagIcon'
import { FullscreenCloseIcon } from '@/atoms/Icons/FullscreenCloseIcon'
import { FullscreenIcon } from '@/atoms/Icons/FullscreenIcon'
import { HeartIconFill } from '@/atoms/Icons/HeartIconFill'
import { HeartIconThick } from '@/atoms/Icons/HeartIconThick'
import { ParagraphMD } from '@/atoms/Text'
import { IconColor } from '@/atoms/utils'
import { useMomentStory } from '@/contexts/MomentStoryContext/MomentStoryContext'
import { useToast } from '@/molecules/Toast'
import { useEngagementDetailOverlay } from '@/organisms/CollectibleSceneInfo/EngagementDetailOverlay'
import { useMomentStoryModal } from '@/organisms/CollectibleSceneInfo/MomentStoryModal'
import { useCollectiblesPlayerEngagement } from '@/organisms/CollectiblesPlayer/CollectiblesPlayerEngagement/CollectiblesPlayerEngagementContext'
import { ShareMoment } from '@/organisms/ShareMoment'
import { Moment } from '@/types/codegen-federation'
import { ScreenType, useScreenType } from '@/utils/ScreenUtils'
import { isTextStory, isVideoStory, PlayableStory } from '@/utils/StoryUtil'
import { useSafeTrack } from '@/utils/analytics'
import { useTranslate } from '@/utils/translate/translate-client'

interface StoryPlayerActionsProps {
  isOverlayMode: boolean
  story: PlayableStory
  onFullscreenToggled: (isFullscreen: boolean) => void
  onReportUserToggled: (isShowing: boolean) => void
}

export const StoryPlayerActions: FC<StoryPlayerActionsProps> = ({
  isOverlayMode,
  onReportUserToggled,
  onFullscreenToggled,
  story: initialStory,
}) => {
  const { t } = useTranslate('watch')
  const track = useSafeTrack()

  const [canShowReportUser, setCanShowReportUser] = useState(false)
  const { showToast } = useToast()
  const { onClose, onOpen, isOpen } = useMomentStoryModal()
  const { engagementMoment } = useCollectiblesPlayerEngagement()
  const { hideMomentFullscreen } = useEngagementDetailOverlay()
  const [isHovered, setIsHovered] = useState(false)
  const [heartColor1, setHeartColor1] = useState<IconColor>(initialStory?.isLikedByUser ? 'red' : 'white')
  const { story, reportUserStoryById, toggleLikeUserStoryById } = useMomentStory()

  const canShowReportUserOverlay = canShowReportUser

  const reportThisStory = t('reportThisStory', 'Report this story')

  useEffect(() => {
    setHeartColor1(story?.isLikedByUser ? 'red' : 'white')
  }, [story, story?.isLikedByUser])

  const handleReportingStoryClicked = useCallback(async () => {
    if (story?.id) {
      await reportUserStoryById()
      track('Report User Story Clicked', {
        storyId: story.id,
      })
      showToast(t('thankYouForReporting', 'Thank you. This story has been reported.'), { shouldHideClose: true })
    }
    setCanShowReportUser(false)
  }, [reportUserStoryById, showToast, story?.id, t, track])

  const handleReportUserToggle = useCallback(() => {
    setCanShowReportUser((current) => {
      const canShow = !current
      onReportUserToggled(canShow)
      return canShow
    })
  }, [onReportUserToggled])

  const handleShowFullScreen = useCallback(() => {
    if (story) onOpen(story)
    onFullscreenToggled(true)
  }, [onFullscreenToggled, onOpen, story])

  const handleCloseFullScreen = useCallback(() => {
    onClose()
    onFullscreenToggled(false)
  }, [onClose, onFullscreenToggled])

  const handleLikeStoryToggle = useCallback(async () => {
    if (story?.id) {
      await toggleLikeUserStoryById()

      const isVideo = isVideoStory(story)
      const isText = isTextStory(story)

      track('MOMENT_STORY_LIKE_CLICKED', {
        storyId: story.id,
        isVideo,
        isText,
      })
    }
  }, [story, toggleLikeUserStoryById, track])

  const screenType = useScreenType()

  const actionButtonsPanelClasses = isOverlayMode ? 'flex-row items-center gap-x-6 justify-end' : 'flex-col gap-y-6'
  const reportUserButtonClasses = isOpen
    ? 'absolute top-1/2 -translate-x-full left-0 -translate-y-1/2'
    : 'absolute top-12 left-0'
  const leftActionButtonClasses = isOverlayMode ? 'flex gap-x-6' : 'flex flex-col gap-y-6'
  const heartActionButtonClasses = isOverlayMode ? 'flex-row items-center gap-x-2' : 'flex-col items-center gap-y-2'

  const shouldShowFullScreen =
    !hideMomentFullscreen && (screenType === ScreenType.desktop || screenType === ScreenType.tablet)

  const canShowMinimizeIcon = isOpen && !isOverlayMode
  const totalLikes = (story?.totalLikes || 0) <= 0 ? '' : story?.totalLikes

  return (
    <div className={classNames('flex justify-between', actionButtonsPanelClasses)}>
      <div className={leftActionButtonClasses}>
        <div className={classNames('flex', heartActionButtonClasses)}>
          <Button onClick={handleLikeStoryToggle} variant="plain">
            <div
              className={classNames({ 'hover:drop-shadow-dark-1 rounded-full': story?.isLikedByUser })}
              onMouseEnter={() => setIsHovered(true)} // Set hover state to true when mouse enters
              onMouseLeave={() => setIsHovered(false)}
            >
              {!story?.isLikedByUser ? (
                isHovered ? (
                  <HeartIconFill size={32} color={heartColor1} />
                ) : (
                  <HeartIconThick size={32} color="white" />
                )
              ) : (
                <HeartIconFill size={32} color={heartColor1} />
              )}
            </div>
          </Button>
          <ParagraphMD weight="bold">{totalLikes}</ParagraphMD>
        </div>
        <div className="h-8 w-8">
          <ShareMoment iconSize={28} moment={engagementMoment as Moment} userId={story?.userId} story={story} />
        </div>
        <div className={classNames('relative w-8 h-8')}>
          {canShowReportUserOverlay && (
            <Button
              onClick={handleReportingStoryClicked}
              outline
              variant="white"
              className={classNames('shadow-dark-1 whitespace-nowrap', reportUserButtonClasses)}
            >
              <ParagraphMD>{reportThisStory}</ParagraphMD>
            </Button>
          )}
          <Button onClick={handleReportUserToggle} variant="plain">
            <FlagIcon size={34} color="white" />
          </Button>
        </div>
      </div>
      {shouldShowFullScreen && (
        <div className="h-8 w-8 pb-6">
          {canShowMinimizeIcon ? (
            <Button onClick={handleCloseFullScreen} variant="plain">
              <FullscreenCloseIcon size={28} color="white" />
            </Button>
          ) : (
            <Button onClick={handleShowFullScreen} variant="plain">
              <FullscreenIcon size={32} color="white" />
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
